import React, { useRef } from 'react'
import ReactDOM from 'react-dom'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
} from '@floating-ui/react'

export interface ReactionUsersPopupProps {
  userNames: string[]
  reactionEmoji: string
  isVisible: boolean
  referenceElement: HTMLElement | null
}

export const ReactionUsersPopup: React.FC<ReactionUsersPopupProps> = ({
  userNames,
  reactionEmoji,
  isVisible,
  referenceElement,
}) => {
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isVisible,
    placement: 'top',
    elements: {
      reference: referenceElement,
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['bottom', 'top-start', 'top-end', 'bottom-start', 'bottom-end'],
      }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  })

  if (!isVisible || !referenceElement || userNames.length === 0) {
    return null
  }

  // Display up to 3 user names
  const displayNames = userNames.slice(0, 3)
  const remainingCount = userNames.length - 3

  const popupContent = (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      className="z-[1000] bg-white rounded-lg shadow-lg p-3 max-w-xs"
    >
      <FloatingArrow
        ref={arrowRef}
        context={context}
        className="fill-white"
        width={12}
        height={6}
      />
      <div className="text-sm">
        <div className="flex flex-wrap items-center gap-1 text-gray-800">
          {displayNames.map((name, index) => (
            <React.Fragment key={index}>
              <span className="font-medium">{name}</span>
              {index < displayNames.length - 1 && <span>,</span>}
            </React.Fragment>
          ))}
          {remainingCount > 0 && (
            <span className="text-gray-600">
              {displayNames.length > 0 && ','} +{remainingCount} other
              {remainingCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="mt-1 text-gray-600 flex items-center gap-1">
          <span>reacted with</span>
          <span className="text-base">{reactionEmoji}</span>
        </div>
      </div>
    </div>
  )

  // Render in a portal to avoid z-index issues
  return ReactDOM.createPortal(popupContent, document.body)
}
